import { Sparkles, Compass, PenLine, TrendingUp } from "lucide-react";
import { Container, Reveal, SectionHeader } from "./common";

const capabilities = [
    {
        icon: Sparkles,
        title: "AI Lead Insights",
        desc: "Identify important leads and surface useful context from activity, source and engagement history.",
    },
    {
        icon: Compass,
        title: "Next Best Action",
        desc: "Help salespeople understand what should happen next on every lead and deal — and why.",
    },
    {
        icon: PenLine,
        title: "Email Assistance",
        desc: "Draft professional, on-context customer communication your team reviews and sends.",
    },
    {
        icon: TrendingUp,
        title: "Deal Insights",
        desc: "Surface useful signals from CRM activity — stalled deals, hot accounts and momentum changes.",
    },
];

const AIVisual = () => (
    <div className="relative mx-auto flex h-[320px] w-full max-w-[420px] items-center justify-center" aria-hidden="true">
        <div className="absolute inset-0 m-auto h-72 w-72 rounded-full border border-[#16A34A]/15" />
        <div className="absolute inset-0 m-auto h-52 w-52 rounded-full border border-[#16A34A]/25" />
        <div className="absolute inset-0 m-auto h-72 w-72 animate-spin-slow rounded-full">
            <span className="absolute -top-1 left-1/2 h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-[#16A34A] shadow-lg shadow-[#16A34A]/40" />
            <span className="absolute bottom-6 right-6 h-2 w-2 rounded-full bg-[#86EFAC]" />
        </div>
        <div className="absolute inset-0 m-auto h-52 w-52 animate-spin-slow rounded-full [animation-direction:reverse] [animation-duration:18s]">
            <span className="absolute left-4 top-4 h-2 w-2 rounded-full bg-[#16A34A]/70" />
        </div>
        <div className="relative flex h-32 w-32 items-center justify-center rounded-3xl bg-gradient-to-br from-[#16A34A] to-[#15803D] shadow-2xl shadow-[#16A34A]/30">
            <Sparkles className="h-10 w-10 text-white" />
            <div className="absolute inset-0 rounded-3xl bg-white/10 [mask-image:linear-gradient(to_bottom_right,black,transparent)]" />
        </div>
        <span className="absolute left-2 top-10 rounded-full border border-[#E5E7EB] bg-white px-3 py-1.5 text-[10px] font-semibold text-[#475569] shadow-md">
            Hot lead · score 92
        </span>
        <span className="absolute bottom-8 right-0 rounded-full border border-[#E5E7EB] bg-white px-3 py-1.5 text-[10px] font-semibold text-[#475569] shadow-md">
            Suggested: schedule demo
        </span>
    </div>
);

const AISection = () => (
    <section id="ai" data-testid="credible-ai-section" className="overflow-hidden bg-[#F7FEF9]/60 py-20 lg:py-28">
        <Container>
            <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
                <div>
                    <SectionHeader
                        align="left"
                        eyebrow="Kryvo Intelligence"
                        title="Smarter selling starts with better context."
                        sub="AI in Kryvo is an assistant, not a gimmick. It works quietly inside your CRM to surface context, suggest next steps and save your team time — your people still close the deals."
                    />
                    <div className="mt-9 grid gap-4 sm:grid-cols-2">
                        {capabilities.map((c, i) => (
                            <Reveal key={c.title} delay={i * 0.06}>
                                <div className="h-full rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-sm transition-all hover:-translate-y-1 hover:border-[#16A34A]/30 hover:shadow-lg">
                                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#F0FDF4]">
                                        <c.icon className="h-4 w-4 text-[#16A34A]" />
                                    </span>
                                    <h3 className="mt-3.5 text-[15px] font-bold text-[#111827]">{c.title}</h3>
                                    <p className="mt-1.5 text-[13px] leading-relaxed text-[#64748B]">{c.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
                <Reveal delay={0.15}>
                    <AIVisual />
                </Reveal>
            </div>
        </Container>
    </section>
);

export default AISection;
